import { useCallback, useEffect, useState } from 'react';

import type { McpGrant } from '@/store/api/mcpMemberApi';

import { useGetMcpConnectGrantsQuery } from '@/store/api/mcpMemberApi';

export const GRANT_POLL_INTERVAL_MS = 3000;
export const GRANT_WAIT_TIMEOUT_MS = 10 * 60 * 1000;

export type GrantWaitStatus = 'idle' | 'waiting' | 'found' | 'timed-out';

export interface GrantWait {
  grant: McpGrant | undefined;
  pollingActive: boolean;
  restart: () => void;
  status: GrantWaitStatus;
}

// Mounted only while waiting: its subscription is what makes RTK Query poll.
// skipPollingIfUnfocused is off because the member is signing in inside
// Claude Desktop while this tab sits in the background.
export const GrantPoller = (): null => {
  useGetMcpConnectGrantsQuery(undefined, {
    pollingInterval: GRANT_POLL_INTERVAL_MS,
    skipPollingIfUnfocused: false,
  });
  return null;
};

export const useGrantWait = (
  baselineIds: readonly string[] | null,
): GrantWait => {
  const { currentData, refetch } = useGetMcpConnectGrantsQuery();
  // Keyed by the baseline array, so a new wait never inherits an old timeout.
  const [timedOutFor, setTimedOutFor] = useState<readonly string[] | null>(
    null,
  );
  const grant = baselineIds
    ? currentData?.grants.find(
        (candidate) => !baselineIds.includes(candidate.id),
      )
    : undefined;

  let status: GrantWaitStatus = 'waiting';
  if (!baselineIds) status = 'idle';
  else if (grant) status = 'found';
  else if (timedOutFor === baselineIds) status = 'timed-out';
  const waiting = status === 'waiting';

  useEffect(() => {
    if (!waiting || !baselineIds) return undefined;
    const timer = window.setTimeout(
      () => setTimedOutFor(baselineIds),
      GRANT_WAIT_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [baselineIds, waiting]);

  const restart = useCallback(() => {
    setTimedOutFor(null);
    refetch().catch(() => undefined);
  }, [refetch]);

  return { grant, pollingActive: waiting, restart, status };
};
